// Centralized geolocation helper that handles cross-browser quirks (iOS Safari,
// Chrome, Firefox, etc.) and surfaces user friendly error messages so callers
// can prompt for permission consistently across the app.

export const GEOLOCATION_ERRORS = {
  NOT_SUPPORTED: "NOT_SUPPORTED",
  INSECURE_CONTEXT: "INSECURE_CONTEXT",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  POSITION_UNAVAILABLE: "POSITION_UNAVAILABLE",
  TIMEOUT: "TIMEOUT",
  UNKNOWN: "UNKNOWN",
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const isSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
};

const isAndroid = () => {
  if (typeof navigator === "undefined") return false;
  return /android/i.test(navigator.userAgent);
};

// Geolocation only works on secure origins. localhost is treated as secure by
// browsers, so we mirror that behaviour here.
export const isGeolocationSecureContext = () => {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const { protocol, hostname } = window.location;
  if (protocol === "https:") return true;
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  ) {
    return true;
  }
  return false;
};

export const getGeolocationErrorMessage = (code) => {
  switch (code) {
    case GEOLOCATION_ERRORS.NOT_SUPPORTED:
      return "Your browser does not support location access. Please enter your address manually.";
    case GEOLOCATION_ERRORS.INSECURE_CONTEXT:
      return "Location access requires a secure (HTTPS) connection. Please open the site over HTTPS or enter your address manually.";
    case GEOLOCATION_ERRORS.PERMISSION_DENIED:
      if (isIOS() && isSafari()) {
        return "Location permission is blocked. On iPhone go to Settings → Privacy & Security → Location Services → Safari Websites → Allow, then refresh this page.";
      }
      if (isIOS()) {
        return "Location permission is blocked. Please enable Location for this browser in iOS Settings, then refresh and try again.";
      }
      if (isAndroid()) {
        return "Location permission is blocked. Tap the lock icon in the address bar → Permissions → Location → Allow, then refresh and try again.";
      }
      return "Location permission is blocked. Please enable location access for this site in your browser settings and try again.";
    case GEOLOCATION_ERRORS.POSITION_UNAVAILABLE:
      return "We could not detect your current location. Please make sure GPS / Location Services are turned on, or enter your address manually.";
    case GEOLOCATION_ERRORS.TIMEOUT:
      return "Detecting your location took too long. Please try again or enter your address manually.";
    default:
      return "We could not get your location. Please try again or enter your address manually.";
  }
};

// Wraps navigator.permissions.query so callers can quickly know whether the
// browser will show the permission prompt or has already denied it. iOS Safari
// only added support for the Permissions API in version 16, so callers should
// treat "unknown" as "we still need to call getCurrentPosition to find out".
export const checkGeolocationPermission = async () => {
  if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
    return "unsupported";
  }
  if (
    !("permissions" in navigator) ||
    typeof navigator.permissions.query !== "function"
  ) {
    return "unknown";
  }
  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result.state; // "granted" | "denied" | "prompt"
  } catch (err) {
    return "unknown";
  }
};

// Promise-based wrapper around navigator.geolocation.getCurrentPosition. The
// returned rejection is always shaped as { code, message, original } so the UI
// layer can match on `code` and display `message` directly.
export const requestGeolocation = (options = {}) => {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      const code = GEOLOCATION_ERRORS.NOT_SUPPORTED;
      return reject({ code, message: getGeolocationErrorMessage(code) });
    }

    if (!isGeolocationSecureContext()) {
      const code = GEOLOCATION_ERRORS.INSECURE_CONTEXT;
      return reject({ code, message: getGeolocationErrorMessage(code) });
    }

    const finalOptions = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 60000,
      ...options,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        let code = GEOLOCATION_ERRORS.UNKNOWN;
        if (error && typeof error.code === "number") {
          if (error.code === 1) code = GEOLOCATION_ERRORS.PERMISSION_DENIED;
          else if (error.code === 2) code = GEOLOCATION_ERRORS.POSITION_UNAVAILABLE;
          else if (error.code === 3) code = GEOLOCATION_ERRORS.TIMEOUT;
        }
        reject({
          code,
          message: getGeolocationErrorMessage(code),
          original: error,
        });
      },
      finalOptions
    );
  });
};

// Haversine distance helper (in kilometres). Used by the cart to validate that
// the chosen address is within the store's delivery radius (e.g. 4 km) without
// waiting for a backend round-trip.
export const getDistanceInKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
